import { TweenLite } from "gsap";
import { Color, CreateElement, LogGroup, Rect, Size } from "../.././MyHelpers";
import { LineWin, PlayWindow } from "../../api";
import { IChangeableInterval, Interval } from "../../Context/Interval";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from "../../ResourceManager/ResourceManager";
import { SpriteResource } from "../../ResourceManager/SpriteResource";
import { Settings } from "../../Settings";
import { ZIndex } from "../../ZIndex";
import { ISpinnableReels } from "../SpinAnimation/SpinnableReels";
import { CanvasSprite } from "../Sprite/CanvasSprite";
import { ISprite } from "../Sprite/ISprite";
import { WinLineMarkers } from "../WinLineMarkers";
import { GenericWinLinesAnimation, WinLinesAnimationParams } from "./GenericWinLinesAnimation";

export interface WinSquareAnimationsParams {
	readonly index: number,
	readonly sprite: ISprite,
	readonly symbolSize: Size,
	readonly spinnableReels: ISpinnableReels,
}
export class WinSquareAnimationsElements {
	protected view: HTMLElement;
	protected symbolSize: Size;
	protected sprite: ISprite;
	protected sprCanvas: CanvasSprite;
	protected currentSquareIndex: number = 0;
	protected lineDuration: number;
	protected index: number;

	protected newScale: number = 1.2;

	//protected columCoordinate: number = 0;
	//protected rowCoordinates: number[] = [];

	protected rects: Rect[] = [];

	public constructor(params: WinSquareAnimationsParams) {
		this.index = params.index;
		this.symbolSize = params.symbolSize;
		this.sprite = params.sprite;
		//let scale: number = 1.25;
		//let symbolSize: Size = spinnableReels.GetSymbolSize();

		// square should match symbol size
		this.sprCanvas = new CanvasSprite({sprite: this.sprite, canvasSize: params.spinnableReels.GetSymbolSize().Scale(this.newScale)});
		//this.sprCanvas.Scale(scale);
		this.sprCanvas.Start(true);

		let columnCenterx = params.spinnableReels.GetSymbolRect(params.index, 0).centerx;

		for (let row = 0; row < params.spinnableReels.GetWindowSymbolSize().Height; row++) {
			let rowCenterY = params.spinnableReels.GetSymbolRect(params.index, row).centery;

			let r = new Rect(
				columnCenterx - (this.symbolSize.Width / 2),
				rowCenterY - (this.symbolSize.Height / 2),
				this.symbolSize.Width,
				this.symbolSize.Height
			).Scale(this.newScale, this.newScale);

			this.rects.push(r);
		}


		// default position before first "play"
		let defaultRect: Rect = this.rects[0];


		this.view = document.createElement("div");
		this.view.classList.add("SquareAnim" + this.index);
		this.view.style.boxSizing = "border-box";
		this.view.appendChild(this.sprCanvas.View());
		this.view.style.position = "absolute";
		this.view.style.top = "0px";
		this.view.style.left = "0px"; // always the same column
		this.view.style.width = this.symbolSize.Scale(this.newScale).Width + "px";
		this.view.style.height = this.symbolSize.Scale(this.newScale).Height + "px";
		this.view.style.zIndex = "" + ZIndex.WinLines;

		TweenLite.set(this.view, { x: defaultRect.x, y: defaultRect.y });



		// just debug stuff
		this.view.style.visibility = "hidden";
		//this.view.style.border = "5px solid red";

	}
	public View(): HTMLElement {
		return this.view;
	}

	public Start(symbolRow: number, firstShowing: boolean) {
		//here we can animate to move to new position
		let rect = this.rects[symbolRow];
		let params = { x: rect.x, y: rect.y };

		// first line show right away, next lines animate
		if (firstShowing) {
			TweenLite.set(this.view, params);
		}
		else {
			TweenLite.to(this.view, 0.125, params);
		}

		this.view.style.visibility = "";

		// restart, so sprites are in sync
		this.sprCanvas.Restart(true);
		
	}

	public Stop(): void {
		this.view.style.visibility = "hidden";
		this.sprCanvas.Stop();
	}

	public Update() {
		this.sprCanvas.Update();
	}
}

export class WinSquareAnimations extends GenericWinLinesAnimation {

	protected sprite: ISprite;
	protected logGroup: LogGroup;
	protected spinnableReels: ISpinnableReels;
	protected resid: ResourceIdentifier;
	protected symbolSize: Size;
	protected viewSize: Size;
	protected slotWindow: Rect = null;
	protected slotWindowSymbolSize: PlayWindow = null;
	protected elements: WinSquareAnimationsElements[] = [];
	protected view: HTMLElement;
	protected lines: number[][];
	protected squareDuration: number;

	protected commonResourcesBasePath: String = Settings.ResourcesFolder + "multigame/slot/common/";


	protected resultLines: LineWin[];
	protected currentResultLineIndex: number = 0;
	protected nextLineInterval: IChangeableInterval = null

	protected readonly animateOnlyWinningSymbols: boolean = true;
	//Create Markers for win Lines
	protected winLineMarkers: WinLineMarkers = null;
	protected running: boolean = false;
	protected speedFactorAnimation: number;

	//constructor(viewSize: Size, slotWindow: PixelRect, slotWindowSymbolSize:Size, spinnableReels: SpinnableReels, logGroup: LogGroup, resid: ResourceIdentifier)
	constructor(params: WinLinesAnimationParams) {
		super();
		this.viewSize = params.viewSize;
		this.spinnableReels = params.spinnableReels;
		this.logGroup = params.logGroup.Add("WinSquaresAnimation");
		this.resid = params.resid.Add("WinSquaresAnimation");
		this.slotWindow = params.spinnableReels.GetWindowRect();
		this.slotWindowSymbolSize = params.spinnableReels.GetWindowSymbolSize();
		//this.speedFactorAnimation = params.speedFactorAnimation;

		this.symbolSize = params.spinnableReels.GetSymbolSize();

		this.lines = params.lines;



		this.view = CreateElement("div", {
			classes: ["WinSquaresAnimation"], style: {
				position: "absolute",
				top: "0px",
				left: "0px",
				width: this.viewSize.Width + "px",
				height: this.viewSize.Height + "px",
				pointerEvents: "none"
			}
		});

		this.squareDuration = params.speedFactorAnimation;

		this.nextLineInterval = new Interval(this.resid, () => this.NextLine(), 2000);
		this.winLineMarkers = new WinLineMarkers(params.slotWindow, this.spinnableReels.GetWindowSymbolSize(), this.lines);
	}

	public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> {
		resourceManager.AddGlobal(new SpriteResource(this.commonResourcesBasePath + "resources/neonsquare", ResourceIdentifier.Global.Add("neonsquare").Add("neonsquare"), true, this.logGroup.Add("neonsquare"), true));
	}

	public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> {
		this.sprite = resourceManager.Get<SpriteResource>(ResourceIdentifier.Global.Add("neonsquare").Add("neonsquare")).sprite;

		// here we have the sprite loaded, we can create Elements (element requires sprite passed via constructor)

		let numberOfColumns = this.spinnableReels.GetWindowSymbolSize().Width;
		for (let i = 0; i < numberOfColumns; i++) {
			let params: WinSquareAnimationsParams = {
				index: i,
				sprite: this.sprite.Copy(),
				symbolSize: this.spinnableReels.GetSymbolSize(),
				spinnableReels: this.spinnableReels
			}
			// create Element
			let element = new WinSquareAnimationsElements(params);

			// save element to array
			this.elements.push(element);

			// append elements view to animations view
			this.view.appendChild(this.winLineMarkers.LeftView());
			this.view.appendChild(this.winLineMarkers.RightView());
			this.view.appendChild(element.View());
		}

	}


	public Start(resultLines: LineWin[], duration: number): void {

		this.running = true;

		if (resultLines.length > 0) {
			// Log.Debug(this.logGroup, "Start");
			this.squareDuration = duration;
			this.resultLines = resultLines;
			this.currentResultLineIndex = 0;

			// TODO use duration for interval!
			this.nextLineInterval.ChangeInterval(this.squareDuration);
			this.nextLineInterval.Start();

			// show first line right await
			this.NextLine();
		}
	}

	protected Stop(interrupted: boolean) {
		this.running = false;
		this.nextLineInterval.Stop();
		for (let column = 0; column < this.elements.length; column++) {
			this.elements[column].Stop();
		}
		this.winLineMarkers.Hide();

		this.OnComplete(interrupted);
	}


	protected NextLine() {

		// check if any lines remaining
		// if not, stop the interval and hide squares

		if (this.currentResultLineIndex >= this.resultLines.length) {
			
			this.Stop(false);
			return;
		}

		// show current line
		let currentResultLine: LineWin = this.resultLines[this.currentResultLineIndex];

		// this holds rows for each column 0, 1, 2, 1, 0
		let currentLine: number[] = this.lines[currentResultLine.LineIndex];

		// Log.Debug(this.logGroup, "Showing line " + this.currentResultLineIndex, currentLine);

		let symbols = currentResultLine.SymbolCount;
		let column = 0

		// show only first symbols
		for (; column < symbols; column++) {
			let row = currentLine[column];

			// here we would tell to what row it should move that particular square
			this.elements[column].Start(row, this.currentResultLineIndex == 0);
		}

		// hide remaining squares
		for (; column < this.elements.length; column++) {
			this.elements[column].Stop();
		}


		if (this.currentResultLineIndex == 0) {
			this.OnStartLine(currentResultLine, Color.FromRGBA(0xDA, 0xAF, 0x37, 0x01), this.animateOnlyWinningSymbols);
			this.winLineMarkers.Show(currentResultLine.LineIndex,Color.FromRGBA(0xDA, 0xAF, 0x37, 0x01))
		}

		this.OnNextLine(currentResultLine, Color.FromRGBA(0xDA, 0xAF, 0x37, 0x01), this.animateOnlyWinningSymbols);
		this.winLineMarkers.Show(currentResultLine.LineIndex,Color.FromRGBA(0xDA, 0xAF, 0x37, 0x01))
		

		this.currentResultLineIndex++;
		//console.log(`Drawing line: ${this.currentResultLineIndex}/${this.resultLines.length}`);

	}

	public RequestStop(): void {
		this.Stop(true);
	}

	// TODO need to check...
	// do we even need that?
	// we have "OnComplete" callback, that signals when its complete
	public GetDuration(): number {
		return this.resultLines.length * this.squareDuration;
	}

	public Update(): void {
		// update sprites, when running
		if (this.running)
		{
			for (let i = 0; i < this.elements.length; i++) {
				this.elements[i].Update();
			}
		}
	}

	public View(): HTMLElement {
		return this.view;
	}
}